/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'
import PermissionIcon from 'src/components/icons/PermissionIcon'
import PermissionUnselectedIcon from 'src/components/icons/PermissionUnselectedIcon'
import FollowingIcon from 'src/components/icons/FollowingIcon'
import StarIcon from 'src/components/icons/StarIcon'
import { MembershipType } from 'src/context/MyScheduleContext/types'

interface MembershipTypeFilterProps {
  selected: MembershipType
  onSelect: (type: MembershipType) => void
  disabled?: boolean
}

interface TabConfig {
  value: MembershipType
  label: string
  Icon?: typeof PermissionIcon | typeof StarIcon | typeof FollowingIcon
  UnselectedIcon?: typeof PermissionUnselectedIcon | typeof FollowingIcon
}

const tabs: TabConfig[] = [
  {
    value: 'both',
    label: 'All',
  },
  {
    value: 'permission',
    label: 'Roles',
    Icon: PermissionIcon,
    UnselectedIcon: PermissionUnselectedIcon,
  },
  {
    value: 'following',
    label: 'Following',
    Icon: StarIcon,
    UnselectedIcon: FollowingIcon,
  },
]

/**
 * MembershipTypeFilter — 3-option segmented control for filtering by membership type.
 * Tabs: All | Permission | Following
 * Styled to match TimePeriodFilter component.
 */
const MembershipTypeFilter: React.FC<MembershipTypeFilterProps> = ({
  selected,
  onSelect,
  disabled = false,
}) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        background-color: transparent;
        border: 1px solid ${colors.SECONDARY_200};
        border-radius: 8px;
        height: 40px;
        opacity: ${disabled ? 0.5 : 1};
        transition: opacity 0.2s ease;
        width: 100%;
        min-width: 380px;
        @media (max-width: 768px) {
          min-width: 0;
        }
      `}
    >
      {tabs.map((tab, index) => {
        const isActive = selected === tab.value
        const { Icon, UnselectedIcon } = tab
        const IconComponent =
          Icon && UnselectedIcon ? (isActive ? Icon : UnselectedIcon) : null
        const isFirst = index === 0
        const isLast = index === tabs.length - 1

        return (
          <button
            key={tab.value}
            type='button'
            disabled={disabled}
            onClick={() => !disabled && onSelect(tab.value)}
            css={css`
              display: flex;
              width: 33.333%;
              min-width: 0;
              align-items: center;
              justify-content: center;
              gap: 6px;
              height: 40px;
              padding: 0 16px;
              white-space: nowrap;
              border-radius: ${isFirst
                ? '8px 0 0 8px'
                : isLast
                ? '0 8px 8px 0'
                : '0'};
              background-color: ${isActive
                ? colors.SECONDARY_300
                : 'transparent'};
              border: none;
              cursor: ${disabled ? 'not-allowed' : 'pointer'};
              transition: all 0.2s ease;
              font-size: 14px;
              font-weight: ${isActive ? 700 : 400};
              line-height: 20.3px;
              color: ${colors.NEUTRAL_0};

              ${!disabled &&
              css`
                &:hover {
                  background-color: ${isActive
                    ? colors.SECONDARY_300
                    : colors.SECONDARY_200};
                }

                &:active {
                  transform: scale(0.98);
                }
              `}
            `}
          >
            {IconComponent && (
              <IconComponent
                size={20}
                color={isActive ? colors.PRIMARY : colors.NEUTRAL_0}
              />
            )}
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

export default MembershipTypeFilter
